import { useState, useEffect, useContext } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { AppContext } from '../App';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Clock, FileText, Download, PieChart as PieIcon, BarChart3, Activity, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { PageSpinner } from '../components/PageSpinner';

const COLORS = ['#6366f1', '#f43f5e', '#f59e0b', '#94a3b8'];

export default function Estadisticas() {
  const { API_URL } = useContext(AppContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('konemed_token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API_URL}/statistics/advanced`, { headers });
      setData(res.data);
    } catch (err) {
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('konemed_token');
      const res = await axios.get(`${API_URL}/statistics/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `estadisticas_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch {
      toast.error('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  const formatMinutes = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return `${h}h ${m}m`;
  };

  if (loading) return <DashboardLayout><PageSpinner /></DashboardLayout>;
  if (!data) return <DashboardLayout><p className="text-center py-20 text-slate-500">No se pudieron cargar las estadísticas</p></DashboardLayout>;

  const pieData = [
    { name: 'Firmados', value: data.signed_count },
    { name: 'Rechazados', value: data.rejected_count },
    { name: 'Pendientes', value: data.pending_count },
    { name: 'Expirados', value: data.expired_count }
  ].filter(d => d.value > 0);

  const signRate = data.total_requests > 0 ? ((data.signed_count / data.total_requests) * 100).toFixed(1) : 0;

  const dailyChart = data.daily_chart.map(d => ({
    ...d,
    label: d.date.slice(5)
  }));

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="estadisticas-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Estadísticas</h1>
            <p className="text-sm text-slate-500 mt-1">Análisis detallado de consentimientos</p>
          </div>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold rounded-xl transition-colors"
            data-testid="export-excel-btn"
          >
            <Download size={18} />
            {exporting ? 'Exportando...' : 'Descargar Excel'}
          </button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <FileText size={20} className="text-indigo-600" />
              </div>
              <span className="text-sm text-slate-500">Total</span>
            </div>
            <p className="text-3xl font-bold text-slate-900" data-testid="stat-total">{data.total_requests}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <TrendingUp size={20} className="text-emerald-600" />
              </div>
              <span className="text-sm text-slate-500">Tasa de Firma</span>
            </div>
            <p className="text-3xl font-bold text-emerald-600" data-testid="stat-sign-rate">{signRate}%</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock size={20} className="text-amber-600" />
              </div>
              <span className="text-sm text-slate-500">Tiempo Promedio</span>
            </div>
            <p className="text-3xl font-bold text-amber-600" data-testid="stat-avg-time">{formatMinutes(data.avg_sign_time_minutes)}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                <Activity size={20} className="text-violet-600" />
              </div>
              <span className="text-sm text-slate-500">Uso Membresía</span>
            </div>
            {data.membership_usage ? (
              <div>
                <p className="text-2xl font-bold text-slate-900">{data.membership_usage.used}<span className="text-base font-normal text-slate-400">/{data.membership_usage.limit}</span></p>
                <div className="mt-2 w-full bg-slate-100 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${data.membership_usage.percentage >= 80 ? 'bg-red-500' : data.membership_usage.percentage >= 50 ? 'bg-amber-500' : 'bg-indigo-500'}`}
                    style={{ width: `${Math.min(data.membership_usage.percentage, 100)}%` }}
                  />
                </div>
                {data.membership_usage.percentage >= 80 && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <AlertTriangle size={12} /> {data.membership_usage.percentage.toFixed(0)}% usado
                  </p>
                )}
              </div>
            ) : (
              <p className="text-lg text-slate-400">N/A</p>
            )}
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Daily Chart */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 size={20} className="text-indigo-600" />
              <h2 className="text-base font-semibold text-slate-900">Firmados últimos 30 días</h2>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} interval={4} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                    formatter={(v) => [v, 'Firmados']}
                    labelFormatter={(l) => `Fecha: ${l}`}
                  />
                  <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Pie Chart */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <PieIcon size={20} className="text-indigo-600" />
              <h2 className="text-base font-semibold text-slate-900">Estado General</h2>
            </div>
            {pieData.length > 0 ? (
              <div className="h-64 flex flex-col items-center">
                <ResponsiveContainer width="100%" height="80%">
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3} dataKey="value">
                      {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '13px' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap justify-center gap-3 mt-2">
                  {pieData.map((d, i) => (
                    <div key={d.name} className="flex items-center gap-1.5 text-xs text-slate-600">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                      {d.name} ({d.value})
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-center text-slate-400 py-16">Sin datos</p>
            )}
          </div>
        </div>

        {/* Template Details Table */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText size={20} className="text-indigo-600" />
            <h2 className="text-base font-semibold text-slate-900">Detalle por Consentimiento</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="template-details-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-semibold text-slate-600">Consentimiento</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-600">Total</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-600">Firmados</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-600">Rechazados</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-600">Pendientes</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-600">Expirados</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-600">Tasa Firma</th>
                </tr>
              </thead>
              <tbody>
                {data.template_details.map((t, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="py-3 px-4 font-medium text-slate-900">{t.name}</td>
                    <td className="text-center py-3 px-4 text-slate-700">{t.total}</td>
                    <td className="text-center py-3 px-4">
                      <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">{t.signed}</span>
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">{t.rejected}</span>
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">{t.pending}</span>
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold">{t.expired}</span>
                    </td>
                    <td className="text-center py-3 px-4 font-semibold text-indigo-600">
                      {t.total > 0 ? `${((t.signed / t.total) * 100).toFixed(0)}%` : '0%'}
                    </td>
                  </tr>
                ))}
                {data.template_details.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-slate-400">No hay consentimientos registrados</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
